/**
 * Lexicon — Language Intelligence Hub
 *
 * Main landing page for the Lexicon feature. Shows aggregated speaker
 * profiles (baseball cards) and the list of analyzed transcripts.
 *
 * Route: /prioritization/lexicon
 */

import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FileText, Plus, RefreshCw, Users,
  TrendingUp, Zap, Brain, ChevronRight,
  AlertCircle, Clock,
} from 'lucide-react';
import { useTenantStore } from '../../../stores/prioritization/tenantStore';
import { PageHeader } from '../../../components/prioritization/PageHeader';
import {
  listTranscripts,
  listSpeakerProfiles,
} from '../../../lib/lexiconService';
import type { LexiconTranscript, SpeakerProfile } from '../../../types/prioritization/lexicon';
import { DIMENSION_ABBREVIATIONS } from '../../../types/prioritization/lexicon';

// ── Helpers ───────────────────────────────────────────────────────────────────

const STATUS_STYLES = {
  pending:    'bg-[var(--hover-tint)] text-[var(--text-sec)]',
  processing: 'bg-[var(--slate-dim)] text-[var(--text-sec)]',
  complete:   'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)]',
  failed:     'bg-[var(--coral-dim)] text-[var(--coral)]',
};

const STATUS_LABELS = {
  pending:    'Pending',
  processing: 'Analyzing…',
  complete:   'Complete',
  failed:     'Failed',
};


function ScoreBar({ value, label }: { value: number | null; label: string }) {
  const pct = value ?? 0;
  const color = pct >= 75 ? 'bg-[var(--green)]' : pct >= 50 ? 'bg-[var(--slate)]' : 'bg-[var(--mustard)]';
  return (
    <div className="space-y-0.5">
      <div className="flex justify-between items-center">
        <span className="text-xs text-[var(--text-ter)]">{label}</span>
        <span className="text-xs font-medium text-[var(--text-sec)]">{value ?? '—'}</span>
      </div>
      <div className="h-1 bg-[var(--hover-tint)] rounded-full overflow-hidden">
        <div className={`h-full rounded-full transition-all ${color}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

// ── Speaker baseball card ─────────────────────────────────────────────────────

function SpeakerCard({
  profile,
  onClick,
}: {
  profile: SpeakerProfile;
  onClick: () => void;
}) {
  const dimensions: Array<[string, number | null]> = [
    [DIMENSION_ABBREVIATIONS.avgStrategicIndex,  profile.avgStrategicIndex],
    [DIMENSION_ABBREVIATIONS.avgMomentum,         profile.avgMomentum],
    [DIMENSION_ABBREVIATIONS.avgAgency,           profile.avgAgency],
    [DIMENSION_ABBREVIATIONS.avgSpecificity,      profile.avgSpecificity],
    [DIMENSION_ABBREVIATIONS.avgClarity,          profile.avgClarity],
    [DIMENSION_ABBREVIATIONS.avgFlexibility,      profile.avgFlexibility],
  ];

  const archetypeColor: string =
    profile.archetype === 'Executor'   ? 'text-[var(--green)] dark:text-[var(--text-ter)] bg-[var(--green-dim)] border-[var(--green)]' :
    profile.archetype === 'Strategist' ? 'text-[var(--text-sec)] bg-[var(--slate-dim)] border-[var(--slate)]' :
    profile.archetype === 'Analyst'    ? 'text-[var(--text-sec)] bg-[var(--slate-dim)] border-[var(--slate)]' :
    profile.archetype === 'Visionary'  ? 'text-[var(--text-sec)] bg-[var(--slate-dim)] border-[var(--slate)]' :
    profile.archetype === 'Connector'  ? 'text-[var(--mustard)] bg-[var(--mustard-dim)] border-[var(--mustard)]' :
    profile.archetype === 'Challenger' ? 'text-[var(--coral)] bg-[var(--coral-dim)] border-[var(--coral)]' :
                                         'text-[var(--text-sec)] bg-[var(--surface)] border-[var(--rule)]';

  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full text-left bg-[var(--bg-raised)] border border-[var(--rule)] p-5 hover:border-[var(--rule)] hover:shadow-sm transition-all group"
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-3">
        <div>
          <p className="text-sm font-semibold text-[var(--text-pri)] group-hover:text-[var(--text-ter)] transition-colors">
            {profile.speakerName}
          </p>
          <p className="text-xs text-[var(--text-ter)] mt-0.5">
            {profile.transcriptCount} transcript{profile.transcriptCount !== 1 ? 's' : ''} ·{' '}
            {(profile.totalWordCount / 1000).toFixed(1)}k words
          </p>
        </div>
        {profile.archetype && profile.archetype !== 'Undefined' && (
          <span className={`text-xs font-medium px-2 py-0.5 rounded-full border ${archetypeColor}`}>
            {profile.archetype}
          </span>
        )}
      </div>

      {/* Stat grid — batting card style */}
      <div className="grid grid-cols-3 gap-2 mb-4">
        {dimensions.map(([abbr, val]) => {
          return (
            <div key={abbr} className="text-center">
              <div className={`text-base font-bold font-mono ${val !== null ? 'text-[var(--text-pri)]' : 'text-[var(--text-ter)]'}`}>
                {val ?? '—'}
              </div>
              <div className="text-xs text-[var(--text-ter)] font-medium">{abbr}</div>
            </div>
          );
        })}
      </div>

      {/* Dimension bars */}
      <div className="space-y-1.5 mb-4">
        {dimensions.map(([abbr, val]) => (
          <ScoreBar key={abbr} value={val} label={abbr} />
        ))}
      </div>

      {/* Action rate */}
      {profile.avgActionCommitmentRate !== null && (
        <div className="flex items-center gap-2 pt-3 border-t border-[var(--rule)]">
          <Zap className="w-3 h-3 text-[var(--mustard)]" />
          <span className="text-xs text-[var(--text-ter)]">
            <span className="font-semibold text-[var(--text-sec)]">
              {Math.round(profile.avgActionCommitmentRate)}%
            </span>{' '}
            action commitment rate
          </span>
        </div>
      )}

      {/* Top topics */}
      {profile.topTopics.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-3">
          {profile.topTopics.slice(0, 4).map(t => (
            <span key={t} className="text-xs bg-[var(--hover-tint)] text-[var(--text-ter)] px-1.5 py-0.5 rounded">
              {t}
            </span>
          ))}
        </div>
      )}

      <div className="flex items-center gap-1 mt-3 text-xs text-[var(--text-ter)] opacity-0 group-hover:opacity-100 transition-opacity">
        View full profile <ChevronRight className="w-3 h-3" />
      </div>
    </button>
  );
}

// ── Transcript row ────────────────────────────────────────────────────────────

function TranscriptRow({
  transcript,
  onView,
}: {
  transcript: LexiconTranscript;
  onView: () => void;
}) {
  const statusStyle = STATUS_STYLES[transcript.analysisStatus];
  const statusLabel = STATUS_LABELS[transcript.analysisStatus];
  const date = transcript.meetingDate
    ? new Date(transcript.meetingDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    : null;

  return (
    <button
      type="button"
      onClick={onView}
      className="w-full text-left flex items-center justify-between px-4 py-3 hover:bg-[var(--surface)] transition-colors group"
    >
      <div className="flex items-center gap-3">
        <FileText className="w-4 h-4 text-[var(--text-ter)] shrink-0" />
        <div>
          <p className="text-sm font-medium text-[var(--text-pri)] group-hover:text-[var(--text-ter)] transition-colors">
            {transcript.title}
          </p>
          <div className="flex items-center gap-2 mt-0.5">
            {date && (
              <span className="text-xs text-[var(--text-ter)] flex items-center gap-1">
                <Clock className="w-2.5 h-2.5" /> {date}
              </span>
            )}
            {transcript.speakers.length > 0 && (
              <span className="text-xs text-[var(--text-ter)]">
                {transcript.speakers.slice(0, 3).join(', ')}
                {transcript.speakers.length > 3 && ` +${transcript.speakers.length - 3} more`}
              </span>
            )}
          </div>
        </div>
      </div>
      <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${statusStyle}`}>
        {statusLabel}
      </span>
    </button>
  );
}

// ── Empty state ───────────────────────────────────────────────────────────────

function EmptyState({ onNew }: { onNew: () => void }) {
  return (
    <div className="text-center py-16 px-6">
      <div className="w-12 h-12 bg-[var(--slate-dim)] flex items-center justify-center mx-auto mb-4">
        <Brain className="w-6 h-6 text-[var(--text-ter)]" />
      </div>
      <h3 className="text-sm font-semibold text-[var(--text-pri)] mb-1">No transcripts yet</h3>
      <p className="text-sm text-[var(--text-ter)] max-w-sm mx-auto mb-6">
        Upload a meeting transcript to start building language intelligence profiles for your team.
      </p>
      <button
        type="button"
        onClick={onNew}
        className="inline-flex items-center gap-1.5 bg-[var(--slate)] hover:bg-[var(--slate)] text-white text-sm font-medium px-4 py-2 transition-colors"
      >
        <Plus className="w-4 h-4" /> Add first transcript
      </button>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function LexiconPage() {
  const navigate = useNavigate();
  const { currentWorkspace } = useTenantStore();
  const workspaceId = currentWorkspace?.id ?? '';

  const [transcripts, setTranscripts] = useState<LexiconTranscript[]>([]);
  const [profiles, setProfiles] = useState<SpeakerProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<'profiles' | 'transcripts'>('profiles');

  const load = useCallback(async () => {
    if (!workspaceId) return;
    setLoading(true);
    setError(null);

    const [txResult, profResult] = await Promise.all([
      listTranscripts(workspaceId),
      listSpeakerProfiles(workspaceId),
    ]);

    if (txResult.error) setError(txResult.error);
    setTranscripts(txResult.data);
    setProfiles(profResult.data);
    setLoading(false);
  }, [workspaceId]);

  useEffect(() => { load(); }, [load]);

  const complete = transcripts.filter(t => t.analysisStatus === 'complete').length;
  const processing = transcripts.filter(t => t.analysisStatus === 'processing').length;

  return (
    <div className="flex-1 overflow-y-auto">
      <div className="max-w-6xl mx-auto px-6 py-6">
        <PageHeader
          title="Lexicon"
          subtitle="Language intelligence — NLP meta-model profiles built from meeting transcripts"
          icon={<Brain className="w-5 h-5" />}
          actions={
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={load}
                className="p-2 text-[var(--text-ter)] hover:text-[var(--text-sec)] hover:bg-[var(--hover-tint)] transition-colors"
                title="Refresh"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              </button>
              <button
                type="button"
                onClick={() => navigate('/prioritization/lexicon/new')}
                className="inline-flex items-center gap-1.5 bg-[var(--slate)] hover:bg-[var(--slate)] text-white text-sm font-medium px-3 py-1.5 transition-colors"
              >
                <Plus className="w-4 h-4" /> Add transcript
              </button>
            </div>
          }
        />

        {/* Experimental banner */}
        <div className="bg-[var(--mustard-dim)] border border-[var(--mustard)] px-4 py-3 mb-6 flex items-start gap-3">
          <AlertCircle className="w-4 h-4 text-[var(--mustard)] shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-[var(--mustard)]">Experimental feature</p>
            <p className="text-xs text-[var(--mustard)] mt-0.5">
              Lexicon uses AI analysis of meeting transcripts to surface communication patterns.
              Profiles improve accuracy with more transcripts. Results are intended as coaching
              prompts, not evaluations.
            </p>
          </div>
        </div>

        {error && (
          <div className="bg-[var(--coral-dim)] border border-[var(--coral)] px-4 py-3 mb-6 text-sm text-[var(--coral)]">
            {error}
          </div>
        )}

        {/* Summary stats */}
        {!loading && transcripts.length > 0 && (
          <div className="grid grid-cols-3 gap-4 mb-6">
            <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-4">
              <div className="flex items-center gap-2 mb-1">
                <FileText className="w-4 h-4 text-[var(--text-ter)]" />
                <span className="text-xs text-[var(--text-ter)]">Transcripts analyzed</span>
              </div>
              <p className="text-2xl font-bold text-[var(--text-pri)]">{complete}</p>
            </div>
            <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-4">
              <div className="flex items-center gap-2 mb-1">
                <Users className="w-4 h-4 text-[var(--text-ter)]" />
                <span className="text-xs text-[var(--text-ter)]">Speaker profiles</span>
              </div>
              <p className="text-2xl font-bold text-[var(--text-pri)]">{profiles.length}</p>
            </div>
            <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-4">
              <div className="flex items-center gap-2 mb-1">
                <TrendingUp className="w-4 h-4 text-[var(--text-ter)]" />
                <span className="text-xs text-[var(--text-ter)]">Processing</span>
              </div>
              <p className="text-2xl font-bold text-[var(--text-pri)]">{processing}</p>
            </div>
          </div>
        )}

        {/* Tab bar */}
        {transcripts.length > 0 && (
          <div className="flex gap-1 mb-5">
            {(['profiles', 'transcripts'] as const).map(t => (
              <button
                key={t}
                type="button"
                onClick={() => setTab(t)}
                className={`text-sm font-medium px-4 py-1.5  transition-colors ${
                  tab === t
                    ? 'bg-[var(--slate-dim)] text-[var(--text-sec)]'
                    : 'text-[var(--text-ter)] hover:text-[var(--text-sec)] hover:bg-[var(--hover-tint)]'
                }`}
              >
                {t === 'profiles' ? (
                  <span className="flex items-center gap-1.5">
                    <Users className="w-3.5 h-3.5" /> Speaker Profiles
                  </span>
                ) : (
                  <span className="flex items-center gap-1.5">
                    <FileText className="w-3.5 h-3.5" /> Transcripts
                  </span>
                )}
              </button>
            ))}
          </div>
        )}

        {/* Content */}
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <RefreshCw className="w-5 h-5 text-[var(--text-ter)] animate-spin" />
          </div>
        ) : transcripts.length === 0 ? (
          <EmptyState onNew={() => navigate('/prioritization/lexicon/new')} />
        ) : tab === 'profiles' ? (
          profiles.length === 0 ? (
            <div className="text-center py-12 text-sm text-[var(--text-ter)]">
              No profiles yet — analyses will appear once transcripts complete processing.
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {profiles.map(p => (
                <SpeakerCard
                  key={p.id}
                  profile={p}
                  onClick={() =>
                    navigate(`/prioritization/lexicon/profile/${encodeURIComponent(p.speakerName)}`)
                  }
                />
              ))}
            </div>
          )
        ) : (
          <div className="bg-[var(--bg-raised)] border border-[var(--rule)] divide-y divide-[var(--rule)] overflow-hidden">
            {transcripts.map(t => (
              <TranscriptRow
                key={t.id}
                transcript={t}
                onView={() => navigate(`/prioritization/lexicon/transcript/${t.id}`)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
